import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

import type { ImportValidationReport } from '@/services/organizationImportService';

interface StepValidationProps {
  report: ImportValidationReport | null;
  loading: boolean;
  error: string | null;
}

export const StepValidation: React.FC<StepValidationProps> = ({ report, loading, error }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="py-10 flex flex-col items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        {t('organizations.import.validate.loading', 'Validation du fichier...')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive flex items-start gap-2">
        <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-semibold">
            {t('organizations.import.validate.parseErrorTitle', 'Le fichier ne peut pas être lu')}
          </div>
          <div className="text-xs mt-1">{error}</div>
        </div>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  const ok = report.ok;

  return (
    <div className="space-y-4">
      <div
        className={`rounded-md border p-4 flex items-start gap-2 ${
          ok
            ? 'border-green-500/40 bg-green-500/5 text-green-700 dark:text-green-400'
            : 'border-destructive/40 bg-destructive/5 text-destructive'
        }`}
      >
        {ok ? (
          <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
        ) : (
          <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1 text-sm">
          <div className="font-semibold">
            {ok
              ? t('organizations.import.validate.okTitle', 'Le fichier est valide')
              : t('organizations.import.validate.errorTitle', 'Erreurs détectées')}
          </div>
          {ok && report.organization_name && (
            <div className="text-xs mt-1 text-foreground/80">
              {t('organizations.import.validate.organizationLabel', 'Organisation')}:{' '}
              <span className="font-medium">{report.organization_name}</span>
            </div>
          )}
        </div>
      </div>

      {ok && (
        <div className="grid grid-cols-3 gap-2 text-center">
          <CountBox label={t('organizations.import.counts.domains', 'Domaines')} value={report.counts.domains} />
          <CountBox label={t('organizations.import.counts.usecases', "Cas d'usage")} value={report.counts.usecases} />
          <CountBox label={t('organizations.import.counts.cadrages', 'Cadrages')} value={report.counts.cadrages} />
        </div>
      )}

      {report.errors.length > 0 && (
        <div className="rounded-md border border-destructive/40 overflow-hidden">
          <div className="bg-destructive/10 px-3 py-2 text-xs font-semibold text-destructive">
            {t('organizations.import.validate.errorsCount', 'Erreurs')}: {report.errors.length}
          </div>
          <ul className="divide-y divide-border max-h-64 overflow-y-auto">
            {report.errors.map((err, i) => (
              <li key={i} className="px-3 py-2 text-xs">
                <code className="text-[11px] bg-muted px-1 py-0.5 rounded mr-2">{err.path}</code>
                <span className="text-foreground">{err.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const CountBox: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="rounded-md border border-border bg-muted/20 py-3">
    <div className="text-2xl font-semibold text-foreground">{value}</div>
    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
  </div>
);

export default StepValidation;
